/**
 * BRE-5369 follow-up — reclaim header bytes from cookies nothing reads anymore.
 *
 * ## Why this exists
 *
 * BRE-5369 moved two things off cookies to shrink the request-header budget:
 * PostHog persistence (~442 B) and the `brev-highlight-*` flags (~99 B).
 * Verified on staging afterwards, **neither byte was actually reclaimed for an
 * existing session**:
 *
 * - Changing PostHog's `persistence` stops it *writing* the cookie. It never
 *   deletes the one already in the jar, which carries a long expiry — so every
 *   existing user keeps paying for it indefinitely.
 * - The `brev-highlight-*` migration lives on `readFlag`, which only runs when
 *   ProductTour code happens to execute. On most pages it never does.
 *
 * A migration that only runs on some read path is not a migration; it is a
 * hope. This sweep runs unconditionally at app boot so the reclamation does
 * not depend on which feature a user happens to touch.
 *
 * ## What it will NOT touch
 *
 * Only cookies this app owns and has already stopped reading. In particular it
 * deletes **only our own** PostHog cookie, keyed by our project token: the
 * marketing site sets a second `ph_<other-token>_posthog` on the parent domain,
 * and removing that would silently destroy someone else's attribution data.
 */

import { createLogger } from "@brev-io/common/src/utils/logger";

const logger = createLogger("Legacy Cookie Sweep");

/** Client-only ProductTour flags, migrated to localStorage in BRE-5369. */
const HIGHLIGHT_PREFIX = "brev-highlight-";

function readCookieMap(): Map<string, string> {
  const out = new Map<string, string>();
  if (typeof document === "undefined") return out;
  for (const pair of document.cookie.split(/;\s*/)) {
    if (!pair) continue;
    const eq = pair.indexOf("=");
    if (eq < 0) continue;
    out.set(pair.slice(0, eq), decodeURIComponent(pair.slice(eq + 1)));
  }
  return out;
}

/**
 * Expire a cookie across the scopes it might have been set on.
 *
 * A cookie can only be deleted with a matching `path`/`domain`, and we do not
 * know which was used — PostHog defaults to a cross-subdomain cookie, while our
 * own flags are host-only. Issuing both forms is harmless (an expiry for a
 * scope that holds no cookie is a no-op) and is the only way to be sure.
 */
function expireCookie(name: string): void {
  const expiry = "Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/";
  document.cookie = `${name}=; ${expiry}`;

  const host = window.location.hostname;
  const labels = host.split(".");
  if (labels.length > 2) {
    // e.g. staging.brev.io -> .brev.io, where a cross-subdomain cookie lives.
    const parent = `.${labels.slice(-2).join(".")}`;
    document.cookie = `${name}=; ${expiry}; Domain=${parent}`;
  }
}

/**
 * Delete cookies the app no longer reads, migrating any value we still need.
 *
 * Returns the names swept, so the caller can log what was actually reclaimed
 * rather than assuming. Never throws: this runs during boot and must not be
 * able to break a page load.
 */
export function sweepLegacyCookies(posthogKey?: string): string[] {
  if (typeof document === "undefined" || typeof window === "undefined") return [];

  const swept: string[] = [];
  try {
    const cookies = readCookieMap();

    for (const [name, value] of cookies) {
      // --- ProductTour highlight flags -------------------------------------
      // Migrate BEFORE deleting. These encode "already dismissed"; dropping the
      // value would re-show a highlight the user has already sent away.
      if (name.startsWith(HIGHLIGHT_PREFIX)) {
        try {
          if (window.localStorage.getItem(name) === null) {
            window.localStorage.setItem(name, value);
          }
        } catch {
          // Storage unavailable (private mode, quota). Keep the cookie: losing
          // the flag is worse than keeping 33 bytes.
          continue;
        }
        expireCookie(name);
        swept.push(name);
        continue;
      }

      // --- Our own PostHog cookie ------------------------------------------
      // Guarded twice. The token match keeps us off the marketing site's
      // cookie, and requiring the localStorage twin proves posthog-js has
      // already carried the identity across — without that proof, deleting the
      // cookie would reset the user's distinct_id.
      if (posthogKey && name === `ph_${posthogKey}_posthog`) {
        try {
          if (window.localStorage.getItem(name) === null) continue;
        } catch {
          continue;
        }
        expireCookie(name);
        swept.push(name);
      }
    }

    if (swept.length > 0) {
      logger.info("[BRE-5369] swept legacy cookies", { names: swept });
    }
  } catch (error) {
    // Diagnostics-adjacent cleanup must never break a page load.
    logger.warn("[BRE-5369] legacy cookie sweep failed", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
  return swept;
}
